import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const CanvasWorkspace = ({ 
  onDrop, 
  onSelectComponent, 
  selectedComponent, 
  components, 
  showAISuggestions = false 
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [zoom, setZoom] = useState(100);
  const canvasRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDraggingOver(false);
    
    const componentData = e?.dataTransfer?.getData('component');
    if (componentData) {
      const component = JSON.parse(componentData);
      const rect = canvasRef?.current?.getBoundingClientRect();
      const x = e?.clientX - rect?.left;
      const y = e?.clientY - rect?.top;
      
      onDrop({
        ...component,
        position: { x, y },
        id: `${component?.id}-${Date.now()}`
      });
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleResetZoom = () => {
    setZoom(100);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <span className="caption font-medium text-foreground">Canvas</span>
          <span className="text-xs text-muted-foreground">
            {components?.length} component{components?.length !== 1 ? 's' : ''}
          </span>
          {showAISuggestions && (
            <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              <Icon name="Sparkles" size={12} />
              AI Active
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label="Zoom out"
          >
            <Icon name="ZoomOut" size={18} />
          </button>
          <button
            onClick={handleResetZoom}
            className="px-3 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
          >
            {zoom}%
          </button>
          <button
            onClick={handleZoomIn}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label="Zoom in"
          >
            <Icon name="ZoomIn" size={18} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
        <div
          ref={canvasRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative mx-auto bg-card border-2 rounded-lg transition-all duration-250 ${
            isDraggingOver 
              ? 'border-primary border-dashed shadow-lg' 
              : 'border-border'
          }`}
          style={{
            width: `${zoom}%`,
            minHeight: '600px',
            maxWidth: '1200px'
          }}
        >
          {components?.length === 0 ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Icon name="MousePointerClick" size={32} className="text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-2">
                Start Building Your Application
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mb-4">
                Drag and drop components from the library to begin creating your web application
              </p>
              {showAISuggestions && (
                <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <Icon name="Sparkles" size={16} className="text-blue-600" />
                  <p className="text-xs text-blue-700">
                    AI assistant is ready to suggest components as you build
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-4">
              {components?.map((component) => (
                <div
                  key={component?.id}
                  onClick={() => onSelectComponent(component)}
                  className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-250 ${
                    selectedComponent?.id === component?.id
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border hover:border-primary/50 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon name={component?.icon} size={24} className="text-primary" />
                    <div className="flex-1">
                      <div className="caption font-semibold text-foreground">
                        {component?.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {component?.description}
                      </div>
                    </div>
                    {selectedComponent?.id === component?.id && (
                      <Icon name="Check" size={20} className="text-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CanvasWorkspace;